import React, { useState, useEffect } from 'react';
import { Check } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { products, fetchProducts, purchasePackage } from '../../services/revenueCatService';
import { useAuth } from '../../contexts/AuthContext';
import { PageHeader } from '../layout/pageHeader';
import type { Product } from '../../types/subscription';
import { Header } from '../layout/Header';

interface Plan extends Product {
    popular?: boolean;
    savings?: string;
}

export default function PricingPlans() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isLoadingPrices, setIsLoadingPrices] = useState(true);

    useEffect(() => {
        const loadProducts = async () => {
            try {
                await fetchProducts();
                setIsLoadingPrices(false);
                // Set yearly plan as default
                setSelectedPlan(products.yearly.id);
            } catch (error) {
                console.error('Error loading products:', error);
                setError('Failed to load subscription plans. Please try again.');
                setIsLoadingPrices(false);
            }
        };

        loadProducts();
    }, []);

    // Calculate yearly savings compared to weekly
    const yearlySavings = React.useMemo(() => {
        if (isLoadingPrices || !products.weekly.rawPrice || !products.yearly.rawPrice) {
            return 0;
        }

        const weeklyCost = products.weekly.rawPrice * 52; // Annual cost if paying weekly
        const yearlyCost = products.yearly.rawPrice;
        
        return Math.round((weeklyCost - yearlyCost) / weeklyCost * 100);
    }, [isLoadingPrices, products.weekly.rawPrice, products.yearly.rawPrice]);

    const plans: Plan[] = React.useMemo(() => {
        // Only include products that have been loaded (have an ID and price)
        const loadedPlans: Plan[] = [];
        
        if (products.weekly.id && products.weekly.price !== 'Loading...') {
            loadedPlans.push({ ...products.weekly });
        }
        
        if (products.yearly.id && products.yearly.price !== 'Loading...') {
            loadedPlans.push({
                ...products.yearly,
                popular: true,
                savings: !isLoadingPrices && yearlySavings > 0 ? `Save ${yearlySavings}%` : undefined
            });
        }

        return loadedPlans;
    }, [products.weekly, products.yearly, isLoadingPrices, yearlySavings]);

    const handleSubscribe = async () => {
        if (isLoading || !user || !selectedPlan) return;
        
        setIsLoading(true);
        
        try {
            await purchasePackage(selectedPlan);
            // After successful purchase, redirect to account page
            navigate('/account');
        } catch (error) {
            console.error('Error selecting plan:', error);
            setError('Failed to process purchase. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center px-4">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 max-w-md w-full">
                    <p className="text-red-600 text-center">{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <Header />
            <div className="py-12 px-4 sm:px-6 lg:px-8">
                <PageHeader
                    headline="Choose Your Plan"
                    subheadline="Start exploring Theos today. Cancel anytime."
                />
                
                <div className="mt-8 max-w-4xl mx-auto">
                    <div className="divide-y divide-gray-200">
                        <div className="py-4 flex items-center">
                            <Check weight="bold" className="w-5 h-5 text-blue-500 mr-3" />
                            <span className="text-gray-700">Access to all Bible study tools and resources</span>
                        </div>
                        <div className="py-4 flex items-center">
                            <Check weight="bold" className="w-5 h-5 text-blue-500 mr-3" />
                            <span className="text-gray-700">Unlimited AI-powered Bible chat conversations</span>
                        </div>
                        <div className="py-4 flex items-center">
                            <Check weight="bold" className="w-5 h-5 text-blue-500 mr-3" />
                            <span className="text-gray-700">Personalized study plans and recommendations</span>
                        </div>
                        <div className="py-4 flex items-center">
                            <Check weight="bold" className="w-5 h-5 text-blue-500 mr-3" />
                            <span className="text-gray-700">Cross-reference and commentary access</span>
                        </div>
                    </div>
                </div>
                
                <div className="mt-12 space-y-4 sm:mt-16 sm:grid sm:grid-cols-2 sm:gap-6 sm:space-y-0 max-w-4xl mx-auto">
                    {plans.map((plan) => (
                        <div
                            key={plan.id}
                            onClick={() => !isLoadingPrices && setSelectedPlan(plan.id)}
                            className={`relative rounded-lg cursor-pointer transition-all duration-200 ${
                                selectedPlan === plan.id
                                    ? 'border-2 border-blue-500 bg-blue-50'
                                    : 'border border-gray-200 hover:border-blue-300 hover:bg-blue-50/50'
                            } ${
                                isLoadingPrices ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                        >
                            {plan.popular && (
                                <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/2">
                                    <span className="inline-flex rounded-full bg-blue-500 px-4 py-1 text-sm font-semibold text-white">
                                        Best Value
                                    </span>
                                </div>
                            )}
                            <div className="p-6">
                                <h2 className="text-2xl font-semibold leading-6 text-gray-900">
                                    {plan.title}
                                </h2>
                                <p className="mt-4 text-sm text-gray-500">
                                    {plan.description}
                                </p>
                                <p className="mt-8">
                                    <span className={`text-4xl font-extrabold text-gray-900 ${isLoadingPrices ? 'opacity-50' : ''}`}>
                                        {plan.price}
                                    </span>
                                    <span className="text-base font-medium text-gray-500">
                                        /{plan.period}
                                    </span>
                                </p>
                                {plan.savings && (
                                    <p className="mt-2 text-sm text-green-600 font-medium">
                                        {plan.savings}
                                    </p>
                                )}
                                {selectedPlan === plan.id && (
                                    <div className="mt-4 flex items-center text-blue-500">
                                        <Check weight="bold" className="w-5 h-5 mr-2" />
                                        <span className="text-sm font-medium">Selected</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                <div className="mt-8 flex justify-center">
                    <button
                        onClick={handleSubscribe}
                        disabled={isLoading || isLoadingPrices || !user || !selectedPlan}
                        className={`px-8 py-3 rounded-md text-white font-medium shadow-sm
                            ${(isLoading || isLoadingPrices || !user || !selectedPlan)
                                ? 'bg-blue-400 cursor-not-allowed'
                                : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
                            }`}
                    >
                        {isLoading ? (
                            <span className="flex items-center">
                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Processing...
                            </span>
                        ) : !user ? (
                            'Sign in to Subscribe'
                        ) : (
                            'Subscribe Now'
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}